import { useMutation } from "@tanstack/react-query";

import { formatDateTimeWithOffset } from "~/shared/utils/dates";
import { timezone } from "~/shared/utils/timezone";
import { ServiceResponse } from "./api.types";
import { api } from "./axios";

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  ad_set_id?: string;
  ad_set_name?: string;
  ad_id?: string;
  ad_name?: string;
}

export interface AppointmentCreateParams {
  providerId: string;
  bookingToken: string;
  dateTime: string;
  preferredOfficeVisit: string | null;
  conditions: string[];
  matchedSpecialties?: string[];
  // UTM and tracking params
  utmParams?: UtmParams | null;
  sendConversionEvent?: boolean;
}

export interface AppointmentResponse {
  id: string;
  providerId: string;
  patientId: string;
  dateTime: string;
  preferredOfficeVisit: string | null;
  location: string;
}

export const createAppointment = async (params: AppointmentCreateParams) => {
  const { utmParams, sendConversionEvent, ...rest } = params;

  const requestData = {
    ...rest,
    dateTime: formatDateTimeWithOffset(params.dateTime),
    bookingToken: params.bookingToken,
    timezone: timezone,
    sendConversionEvent: sendConversionEvent ?? false,
    // Flatten UTM params into request
    ...(utmParams?.utm_source && { utm_source: utmParams.utm_source }),
    ...(utmParams?.utm_medium && { utm_medium: utmParams.utm_medium }),
    ...(utmParams?.utm_campaign && { utm_campaign: utmParams.utm_campaign }),
    ...(utmParams?.utm_term && { utm_term: utmParams.utm_term }),
    ...(utmParams?.utm_content && { utm_content: utmParams.utm_content }),
    ...(utmParams?.fbclid && { fbclid: utmParams.fbclid }),
    ...(utmParams?.fbc && { fbc: utmParams.fbc }),
    ...(utmParams?.fbp && { fbp: utmParams.fbp }),
    ...(utmParams?.ad_set_id && { ad_set_id: utmParams.ad_set_id }),
    ...(utmParams?.ad_set_name && { ad_set_name: utmParams.ad_set_name }),
    ...(utmParams?.ad_id && { ad_id: utmParams.ad_id }),
    ...(utmParams?.ad_name && { ad_name: utmParams.ad_name }),
  };

  const { data } = await api.post<ServiceResponse<AppointmentResponse>>(
    "appointments",
    requestData,
  );

  return data.data;
};

export const useCreateAppointment = () => {
  return useMutation({
    mutationFn: createAppointment,
  });
};
